import { useCallback, useEffect, useMemo, useState } from "react";
import {
  callExternalMcpContextIntent,
  createHostedMcpToken,
  discoverExternalMcpTools,
  getMcpEngineeringContext,
  getMcpProjectSnapshotStatus,
  getMcpStatus,
  listHostedMcpTokens,
  listMcpEngineeringArtifacts,
  listMcpTools,
  publishMcpProjectSnapshot,
  resolveExternalMcpCapabilities,
  revokeHostedMcpToken,
} from "../lib/mcpProductClient";
import {
  clearGitHubMcpUserConnector,
  getGitHubMcpContextEnabled,
  getGitHubMcpRepositories,
  getGitHubMcpUserConnectorMetadata,
  getSelectedGitHubMcpConnectorId,
  getSelectedGitHubMcpRepository,
  listGitHubMcpConnectorCandidates,
  MCP_GITHUB_DEFAULT_ENDPOINT,
  MCP_GITHUB_USER_CONNECTOR_ID,
  notifyGitHubMcpConnectorChanged,
  notifyGitHubMcpRepositoryChanged,
  removeGitHubMcpRepository,
  selectGitHubMcpRepository,
  setGitHubMcpContextEnabled,
  setGitHubMcpUserConnector,
  setSelectedGitHubMcpConnectorId,
  upsertGitHubMcpRepository,
} from "../lib/mcpConnectionPrefs";
import {
  MCP_SNAPSHOT_PUBLISHED_EVENT,
  MCP_SNAPSHOT_PUBLISH_ERROR_EVENT,
  getMcpAutoPublishEnabled,
  getMcpAutoPublishScope,
  setMcpAutoPublishEnabled,
  setMcpAutoPublishScope,
} from "../lib/mcpAutoPublishPrefs";

function StatusPill({ active, label }) {
  return (
    <span className={[
      "inline-flex max-w-full items-center rounded-full px-2 py-1 text-xs font-medium",
      active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600",
    ].join(" ")}>
      <span className="min-w-0 truncate">{label}</span>
    </span>
  );
}

function HealthItem({ ok, label, detail }) {
  return (
    <div className="min-w-0 rounded border border-slate-200 bg-white p-2">
      <div className="flex items-center gap-2 text-sm">
        <span className={[
          "inline-block h-2.5 w-2.5 shrink-0 rounded-full",
          ok ? "bg-emerald-500" : "bg-amber-400",
        ].join(" ")} />
        <span className="min-w-0 break-words font-medium text-slate-900">{label}</span>
      </div>
      {detail && <div className="mt-1 break-words text-xs text-slate-500">{detail}</div>}
    </div>
  );
}

function connectorLabel(connector) {
  return [connector?.name, connector?.id ? `(${connector.id})` : ""].filter(Boolean).join(" ");
}

function repoHintFromLocalStorage(storage = localStorage) {
  try {
    const selected = getSelectedGitHubMcpRepository(storage);
    if (selected?.owner && selected?.repo) {
      return {
        owner: selected.owner,
        repo: selected.repo,
      };
    }
    return {
      owner: storage.getItem("repoOwner") || "",
      repo: storage.getItem("repoName") || "",
    };
  } catch {
    return { owner: "", repo: "" };
  }
}

function previewText(value, max = 420) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  return text.length > max ? `${text.slice(0, max)}...` : text;
}

function githubContextErrorMessage(error) {
  const message = error?.message || "GitHub MCP context test failed.";
  if (/404|not found|422|permission|permissions|cannot be searched|does not exist/i.test(message)) {
    return `${message} Check that the repository is spelled as owner/repo, that private repos are visible to this token, and that the token includes read-only repository access.`;
  }
  return message;
}

function parseRepoDraft(value = "") {
  const text = String(value || "").trim();
  if (!text) return null;
  try {
    const url = new URL(text);
    if (!url.hostname.toLowerCase().includes("github.com")) return null;
    const [owner, repo] = url.pathname.split("/").filter(Boolean);
    return owner && repo ? { owner, repo: repo.replace(/\.git$/i, "") } : null;
  } catch {
    const match = text.match(/^([^/\s]+)\/([^/\s]+)$/);
    return match ? { owner: match[1], repo: match[2].replace(/\.git$/i, "") } : null;
  }
}

function repoRootPath() {
  return "/path/to/xHandle-Lite";
}

function buildClaudeDesktopConfig(rootPath = repoRootPath()) {
  return JSON.stringify({
    mcpServers: {
      "xhandle-lite": {
        command: "node",
        args: [
          `${rootPath}/server/mcpStdioServerEntry.js`,
        ],
        cwd: rootPath,
      },
    },
  }, null, 2);
}

function resolveHostedBackendUrl() {
  if (typeof window !== "undefined") {
    return window.__BACKEND_URL || localStorage.getItem("backendURL") || "";
  }
  return process.env.REACT_APP_BACKEND_URL || "http://localhost:5001";
}

function hostedMcpUrl(baseUrl = resolveHostedBackendUrl()) {
  return `${String(baseUrl || "").replace(/\/+$/g, "")}/mcp`;
}

function buildHostedClaudeDesktopConfig(rootPath = repoRootPath(), token = "paste_xhmcp_token_here") {
  return JSON.stringify({
    mcpServers: {
      "xhandle-lite": {
        command: "node",
        args: [
          `${rootPath}/server/mcpRemoteStdioServerEntry.js`,
        ],
        cwd: rootPath,
        env: {
          XHANDLE_MCP_REMOTE_URL: hostedMcpUrl(),
          XHANDLE_MCP_TOKEN: token || "paste_xhmcp_token_here",
        },
      },
    },
  }, null, 2);
}

function buildCodexMcpConfig(rootPath = repoRootPath()) {
  return [
    "[mcp_servers.xhandle-lite]",
    'command = "node"',
    `args = ["${rootPath}/server/mcpStdioServerEntry.js"]`,
    `cwd = "${rootPath}"`,
  ].join("\n");
}

function buildHostedCodexMcpConfig(rootPath = repoRootPath(), token = "paste_xhmcp_token_here") {
  return [
    "[mcp_servers.xhandle-lite]",
    'command = "node"',
    `args = ["${rootPath}/server/mcpRemoteStdioServerEntry.js"]`,
    `cwd = "${rootPath}"`,
    `env = { XHANDLE_MCP_REMOTE_URL = "${hostedMcpUrl()}", XHANDLE_MCP_TOKEN = "${token || "paste_xhmcp_token_here"}" }`,
  ].join("\n");
}

const CLAUDE_DESKTOP_CONFIG_PATH = "~/Library/Application Support/Claude/claude_desktop_config.json";
const CODEX_MCP_CONFIG_HINT = "~/.codex/config.toml or trusted project .codex/config.toml";
const CLAUDE_VALIDATION_PROMPTS = [
  "Use xHandle to summarize the project context.",
  "What xHandle engineering artifact categories are available?",
  "Use xHandle to explain the traceability context.",
  "Which xHandle workflows relate hazards to requirements and verification?",
  "What does xHandle expose through MCP, and what does it intentionally not expose?",
];
const CODEX_VALIDATION_PROMPTS = [
  "Use xHandle MCP to list published workspace projects before proposing a code change.",
  "Search xHandle workspace artifacts for the component or requirement related to the file I am editing.",
  "Use xHandle MCP to identify hazards or verification artifacts that could be affected by this change.",
  "Use xHandle MCP to fetch detail for one relevant artifact and summarize the engineering constraints for this code task.",
];
const REQUIRED_CLAUDE_MCP_TOOLS = [
  "get_project_context",
  "list_engineering_artifacts",
  "get_engineering_context",
  "list_project_artifacts",
  "get_project_artifact_summary",
  "get_project_artifact_detail",
  "list_workspace_projects",
  "search_workspace_artifacts",
  "get_workspace_artifact_detail",
  "get_project_traceability_context",
];

const MCP_SETTINGS_SUBVIEWS = [
  { id: "hosted", label: "Hosted Clients" },
  { id: "github", label: "GitHub Context" },
  { id: "local", label: "Local Clients" },
  { id: "diagnostics", label: "Diagnostics" },
];

async function copyTextToClipboard(text) {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  return false;
}

export default function McpConnectionsPanel({ getProjectSnapshot } = {}) {
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState("");
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [discoveryResult, setDiscoveryResult] = useState(null);
  const [capabilityResult, setCapabilityResult] = useState(null);
  const [discoveryError, setDiscoveryError] = useState("");
  const [githubTesting, setGithubTesting] = useState(false);
  const [githubTestResult, setGithubTestResult] = useState(null);
  const [githubTestError, setGithubTestError] = useState("");
  const [selectedGitHubConnectorId, setSelectedGitHubConnectorIdState] = useState(
    () => getSelectedGitHubMcpConnectorId()
  );
  const [githubRepositories, setGithubRepositories] = useState(() => getGitHubMcpRepositories());
  const [githubUserConnector, setGithubUserConnector] = useState(() => getGitHubMcpUserConnectorMetadata());
  const [githubContextEnabled, setGithubContextEnabledState] = useState(() => getGitHubMcpContextEnabled());
  const [githubEndpointDraft, setGithubEndpointDraft] = useState(() => getGitHubMcpUserConnectorMetadata().url || MCP_GITHUB_DEFAULT_ENDPOINT);
  const [githubTokenDraft, setGithubTokenDraft] = useState("");
  const [repoDraft, setRepoDraft] = useState("");
  const [claudeCopyMessage, setClaudeCopyMessage] = useState("");
  const [claudeValidationBusy, setClaudeValidationBusy] = useState(false);
  const [claudeValidationResult, setClaudeValidationResult] = useState(null);
  const [claudeValidationError, setClaudeValidationError] = useState("");
  const [projectPublishBusy, setProjectPublishBusy] = useState(false);
  const [projectPublishError, setProjectPublishError] = useState("");
  const [snapshotStatus, setSnapshotStatus] = useState(null);
  const [snapshotStatusError, setSnapshotStatusError] = useState("");
  const [hostedTokens, setHostedTokens] = useState([]);
  const [hostedTokenName, setHostedTokenName] = useState("Claude Desktop");
  const [hostedTokenBusy, setHostedTokenBusy] = useState(false);
  const [hostedTokenError, setHostedTokenError] = useState("");
  const [generatedHostedToken, setGeneratedHostedToken] = useState("");
  const [autoPublishEnabled, setAutoPublishEnabledState] = useState(() => getMcpAutoPublishEnabled());
  const [autoPublishScope, setAutoPublishScopeState] = useState(() => getMcpAutoPublishScope());
  const [activeMcpSubview, setActiveMcpSubview] = useState("hosted");
  const [installPath, setInstallPath] = useState(() => repoRootPath());
  const selectedGitHubRepository = useMemo(
    () => githubRepositories.find((repo) => repo.selected) || githubRepositories[0] || null,
    [githubRepositories]
  );
  const normalizedInstallPath = installPath.trim() || repoRootPath();
  const claudeConfig = useMemo(() => buildClaudeDesktopConfig(normalizedInstallPath), [normalizedInstallPath]);
  const hostedClaudeConfig = useMemo(() => buildHostedClaudeDesktopConfig(normalizedInstallPath, generatedHostedToken), [normalizedInstallPath, generatedHostedToken]);
  const claudePromptText = useMemo(() => CLAUDE_VALIDATION_PROMPTS.join("\n"), []);
  const codexConfig = useMemo(() => buildCodexMcpConfig(normalizedInstallPath), [normalizedInstallPath]);
  const hostedCodexConfig = useMemo(() => buildHostedCodexMcpConfig(normalizedInstallPath, generatedHostedToken), [normalizedInstallPath, generatedHostedToken]);
  const codexPromptText = useMemo(() => CODEX_VALIDATION_PROMPTS.join("\n"), []);
  const legacyRepoHint = repoHintFromLocalStorage();
  const hasGitHubRepoHint = Boolean(selectedGitHubRepository || (legacyRepoHint.owner && legacyRepoHint.repo));

  async function loadStatus() {
    setLoading(true);
    setStatusError("");
    try {
      setStatus(await getMcpStatus());
    } catch (error) {
      setStatusError(error?.message || "Could not load MCP status.");
    } finally {
      setLoading(false);
    }
  }

  async function loadSnapshotStatus() {
    setSnapshotStatusError("");
    try {
      setSnapshotStatus(await getMcpProjectSnapshotStatus());
    } catch (error) {
      setSnapshotStatus(null);
      setSnapshotStatusError(error?.message || "Could not load published MCP context status.");
    }
  }

  async function loadHostedTokens() {
    setHostedTokenError("");
    try {
      setHostedTokens(await listHostedMcpTokens());
    } catch (error) {
      setHostedTokens([]);
      setHostedTokenError(error?.message || "Could not load hosted MCP tokens.");
    }
  }

  async function testDiscovery() {
    setDiscovering(true);
    setDiscoveryError("");
    setDiscoveryResult(null);
    setCapabilityResult(null);
    try {
      const [tools, capabilities] = await Promise.all([
        discoverExternalMcpTools(),
        resolveExternalMcpCapabilities(),
      ]);
      setDiscoveryResult(tools);
      setCapabilityResult(capabilities);
    } catch (error) {
      setDiscoveryError(error?.message || "External MCP discovery failed.");
    } finally {
      setDiscovering(false);
    }
  }

  async function testGitHubContext() {
    setGithubTesting(true);
    setGithubTestError("");
    setGithubTestResult(null);
    try {
      const repoHint = selectedGitHubRepository
        ? { owner: selectedGitHubRepository.owner, repo: selectedGitHubRepository.repo }
        : repoHintFromLocalStorage();
      if (!selectedGitHubConnectorId) {
        throw new Error("Select a GitHub MCP connector first.");
      }
      if (!repoHint.owner || !repoHint.repo) {
        throw new Error("Configure a GitHub repo owner and repo name before testing MCP context.");
      }

      const [summary, activity] = await Promise.all([
        callExternalMcpContextIntent(
          selectedGitHubConnectorId,
          "repo_summary",
          { owner: repoHint.owner, repo: repoHint.repo }
        ),
        callExternalMcpContextIntent(
          selectedGitHubConnectorId,
          "recent_activity",
          { owner: repoHint.owner, repo: repoHint.repo, limit: 5 }
        ),
      ]);
      setGithubTestResult({
        repository: `${repoHint.owner}/${repoHint.repo}`,
        contexts: [
          {
            label: "Repo summary",
            text: summary?.context?.text || "",
          },
          {
            label: "Recent activity",
            text: activity?.context?.text || "",
          },
        ],
      });
    } catch (error) {
      setGithubTestError(githubContextErrorMessage(error));
    } finally {
      setGithubTesting(false);
    }
  }

  async function copyClaudeConfig() {
    setClaudeCopyMessage("");
    try {
      const copied = await copyTextToClipboard(claudeConfig);
      setClaudeCopyMessage(copied ? "Claude config copied." : "Copy unavailable. Select and copy the config manually.");
    } catch {
      setClaudeCopyMessage("Copy unavailable. Select and copy the config manually.");
    }
  }

  async function copyHostedClaudeConfig() {
    setClaudeCopyMessage("");
    try {
      const copied = await copyTextToClipboard(hostedClaudeConfig);
      setClaudeCopyMessage(copied ? "Hosted Claude config copied." : "Copy unavailable. Select and copy the config manually.");
    } catch {
      setClaudeCopyMessage("Copy unavailable. Select and copy the config manually.");
    }
  }

  async function copyHostedCodexConfig() {
    setClaudeCopyMessage("");
    try {
      const copied = await copyTextToClipboard(hostedCodexConfig);
      setClaudeCopyMessage(copied ? "Hosted Codex config copied." : "Copy unavailable. Select and copy the config manually.");
    } catch {
      setClaudeCopyMessage("Copy unavailable. Select and copy the config manually.");
    }
  }

  async function copyClaudePrompts() {
    setClaudeCopyMessage("");
    try {
      const copied = await copyTextToClipboard(claudePromptText);
      setClaudeCopyMessage(copied ? "Validation prompts copied." : "Copy unavailable. Select and copy the prompts manually.");
    } catch {
      setClaudeCopyMessage("Copy unavailable. Select and copy the prompts manually.");
    }
  }

  async function copyCodexConfig() {
    setClaudeCopyMessage("");
    try {
      const copied = await copyTextToClipboard(codexConfig);
      setClaudeCopyMessage(copied ? "Codex config copied." : "Copy unavailable. Select and copy the config manually.");
    } catch {
      setClaudeCopyMessage("Copy unavailable. Select and copy the config manually.");
    }
  }

  async function copyCodexPrompts() {
    setClaudeCopyMessage("");
    try {
      const copied = await copyTextToClipboard(codexPromptText);
      setClaudeCopyMessage(copied ? "Codex prompts copied." : "Copy unavailable. Select and copy the prompts manually.");
    } catch {
      setClaudeCopyMessage("Copy unavailable. Select and copy the prompts manually.");
    }
  }

  async function validateClaudeTools() {
    setClaudeValidationBusy(true);
    setClaudeValidationError("");
    setClaudeValidationResult(null);
    try {
      const [tools, artifacts, traceabilityContext] = await Promise.all([
        listMcpTools(),
        listMcpEngineeringArtifacts(),
        getMcpEngineeringContext({ artifactType: "traceability", includeExamples: true }),
      ]);
      const toolNames = tools.map((tool) => tool.name);
      const missingTools = REQUIRED_CLAUDE_MCP_TOOLS.filter((name) => !toolNames.includes(name));
      const artifactIds = artifacts?.structuredContent?.artifacts?.map((artifact) => artifact.id) || [];
      const traceabilityReady = Boolean(
        traceabilityContext?.structuredContent?.artifacts?.some((artifact) => artifact.id === "traceability")
      );

      if (missingTools.length) {
        throw new Error(`Missing Claude MCP tool(s): ${missingTools.join(", ")}`);
      }
      if (!artifactIds.includes("traceability") || !traceabilityReady) {
        throw new Error("Traceability engineering context was not available.");
      }

      setClaudeValidationResult({
        toolCount: toolNames.length,
        artifactCount: artifactIds.length,
        traceabilityReady,
      });
    } catch (error) {
      setClaudeValidationError(error?.message || "Claude Desktop MCP validation failed.");
    } finally {
      setClaudeValidationBusy(false);
    }
  }

  const publishProjectSnapshot = useCallback(async (scope = "project") => {
    setProjectPublishBusy(true);
    setProjectPublishError("");
    try {
      if (typeof getProjectSnapshot !== "function") {
        throw new Error("Project context is not available in this xHandle view.");
      }
      const snapshot = await getProjectSnapshot({ scope });
      if (!snapshot || typeof snapshot !== "object") {
        throw new Error("No active xHandle project context is available to publish.");
      }
      const result = await publishMcpProjectSnapshot({
        ...snapshot,
        mcpPublishScope: scope === "workspace" ? "workspace" : "project",
      });
      setSnapshotStatus({
        ...result,
        published: true,
      });
    } catch (error) {
      setProjectPublishError(error?.message || "Could not publish project context for MCP clients.");
    } finally {
      setProjectPublishBusy(false);
    }
  }, [getProjectSnapshot]);

  useEffect(() => {
    loadStatus();
    loadSnapshotStatus();
    loadHostedTokens();
  }, []);

  const createHostedToken = useCallback(async () => {
    setHostedTokenBusy(true);
    setHostedTokenError("");
    setGeneratedHostedToken("");
    try {
      const result = await createHostedMcpToken({
        name: hostedTokenName.trim() || "External MCP client",
        scopes: ["mcp:read"],
      });
      setGeneratedHostedToken(result.token || "");
      await loadHostedTokens();
    } catch (error) {
      setHostedTokenError(error?.message || "Could not create hosted MCP token.");
    } finally {
      setHostedTokenBusy(false);
    }
  }, [hostedTokenName]);

  const revokeHostedToken = useCallback(async (tokenId) => {
    setHostedTokenBusy(true);
    setHostedTokenError("");
    try {
      await revokeHostedMcpToken(tokenId);
      await loadHostedTokens();
    } catch (error) {
      setHostedTokenError(error?.message || "Could not revoke hosted MCP token.");
    } finally {
      setHostedTokenBusy(false);
    }
  }, []);

  useEffect(() => {
    const onPublished = (event) => {
      setProjectPublishError("");
      setSnapshotStatus({
        ...(event.detail || {}),
        published: true,
      });
    };
    const onPublishError = (event) => {
      setProjectPublishError(event.detail?.message || "Could not automatically publish MCP context.");
    };
    window.addEventListener(MCP_SNAPSHOT_PUBLISHED_EVENT, onPublished);
    window.addEventListener(MCP_SNAPSHOT_PUBLISH_ERROR_EVENT, onPublishError);
    return () => {
      window.removeEventListener(MCP_SNAPSHOT_PUBLISHED_EVENT, onPublished);
      window.removeEventListener(MCP_SNAPSHOT_PUBLISH_ERROR_EVENT, onPublishError);
    };
  }, []);

  const toggleAutoPublish = useCallback((enabled) => {
    setAutoPublishEnabledState(setMcpAutoPublishEnabled(enabled));
    if (enabled) {
      publishProjectSnapshot(autoPublishScope);
    }
  }, [autoPublishScope, publishProjectSnapshot]);

  const chooseAutoPublishScope = useCallback((scope) => {
    const normalized = setMcpAutoPublishScope(scope);
    setAutoPublishScopeState(normalized);
    if (autoPublishEnabled) {
      publishProjectSnapshot(normalized);
    }
  }, [autoPublishEnabled, publishProjectSnapshot]);

  const connectors = useMemo(() => status?.externalConnectors || [], [status]);
  const githubConnectors = useMemo(() => {
    const candidates = listGitHubMcpConnectorCandidates(connectors);
    if (!githubUserConnector.hasToken) return candidates;
    const existing = candidates.some((connector) => connector.id === githubUserConnector.id);
    return existing ? candidates : [
      {
        id: githubUserConnector.id,
        name: githubUserConnector.name,
        url: githubUserConnector.url,
        source: "local",
        allowedTools: githubUserConnector.allowedTools,
      },
      ...candidates,
    ];
  }, [connectors, githubUserConnector]);
  const selectedGitHubConnector = useMemo(
    () => githubConnectors.find((connector) => connector.id === selectedGitHubConnectorId) || null,
    [githubConnectors, selectedGitHubConnectorId]
  );
  const mcpHealthItems = useMemo(() => ([
    {
      ok: !!status?.ok,
      label: "Product API",
      detail: status?.ok ? "Ready for product MCP routes." : "Backend status has not reported ready.",
    },
    {
      ok: !!snapshotStatus?.published,
      label: "xHandle context",
      detail: snapshotStatus?.published
        ? `${snapshotStatus.scope || "project"} snapshot published${snapshotStatus.publishedAt ? ` at ${snapshotStatus.publishedAt}` : ""}.`
        : "Automatic publishing keeps local MCP clients supplied with project context.",
    },
    {
      ok: hostedTokens.some((token) => !token.revokedAt),
      label: "Hosted MCP token",
      detail: hostedTokens.some((token) => !token.revokedAt) ? "A scoped hosted token is available." : "Create a Supabase-backed token for Claude or VS Code.",
    },
    {
      ok: githubContextEnabled,
      label: "GitHub context",
      detail: githubContextEnabled ? "Collaborator can request selected repo context." : "Disabled until you opt in.",
    },
    {
      ok: githubUserConnector.hasToken,
      label: "GitHub token",
      detail: githubUserConnector.hasToken ? `Saved locally as ....${githubUserConnector.last4}.` : "Paste the full token, including its prefix, if using GitHub MCP.",
    },
    {
      ok: !!selectedGitHubRepository,
      label: "GitHub repo",
      detail: selectedGitHubRepository ? selectedGitHubRepository.label : "Add owner/repo or a GitHub URL.",
    },
    {
      ok: !!selectedGitHubConnector,
      label: "GitHub connector",
      detail: selectedGitHubConnector ? connectorLabel(selectedGitHubConnector) : "Select a GitHub-capable connector.",
    },
  ]), [githubContextEnabled, githubUserConnector, hostedTokens, selectedGitHubConnector, selectedGitHubRepository, snapshotStatus, status]);

  const selectGitHubConnector = useCallback((connectorId) => {
    const next = setSelectedGitHubMcpConnectorId(connectorId);
    setSelectedGitHubConnectorIdState(next);
    if (next) {
      setGithubContextEnabledState(setGitHubMcpContextEnabled(true));
    }
    notifyGitHubMcpConnectorChanged(next);
  }, []);

  const saveGitHubUserConnector = useCallback(() => {
    const endpoint = githubEndpointDraft.trim() || MCP_GITHUB_DEFAULT_ENDPOINT;
    if (!githubTokenDraft.trim()) {
      setGithubTestError("Enter a GitHub MCP token before saving the user connector.");
      return;
    }
    const saved = setGitHubMcpUserConnector({
      url: endpoint,
      token: githubTokenDraft,
    });
    setGithubUserConnector(getGitHubMcpUserConnectorMetadata());
    setGithubEndpointDraft(saved.url);
    setGithubTokenDraft("");
    selectGitHubConnector(MCP_GITHUB_USER_CONNECTOR_ID);
    setGithubTestError("");
  }, [githubEndpointDraft, githubTokenDraft, selectGitHubConnector]);

  const clearGitHubUserConnector = useCallback(() => {
    clearGitHubMcpUserConnector();
    setGithubUserConnector(getGitHubMcpUserConnectorMetadata());
    setGithubEndpointDraft(MCP_GITHUB_DEFAULT_ENDPOINT);
    setGithubTokenDraft("");
    if (selectedGitHubConnectorId === MCP_GITHUB_USER_CONNECTOR_ID) {
      selectGitHubConnector("");
    }
  }, [selectGitHubConnector, selectedGitHubConnectorId]);

  const toggleGitHubContextEnabled = useCallback((enabled) => {
    setGithubContextEnabledState(setGitHubMcpContextEnabled(enabled));
    notifyGitHubMcpRepositoryChanged(getSelectedGitHubMcpRepository());
  }, []);

  const refreshGitHubRepositories = useCallback(() => {
    setGithubRepositories(getGitHubMcpRepositories());
    setGithubUserConnector(getGitHubMcpUserConnectorMetadata());
    setGithubContextEnabledState(getGitHubMcpContextEnabled());
  }, []);

  const publishRepositoryChange = useCallback((repositories) => {
    const selected = repositories.find((repo) => repo.selected) || repositories[0] || null;
    setGithubRepositories(repositories);
    notifyGitHubMcpRepositoryChanged(selected);
  }, []);

  const addGitHubRepository = useCallback(() => {
    const parsed = parseRepoDraft(repoDraft);
    if (!parsed) {
      setGithubTestError("Enter a GitHub repository as owner/repo or a GitHub URL.");
      return;
    }
    setGithubTestError("");
    const repositories = upsertGitHubMcpRepository({ ...parsed, selected: true });
    setGithubContextEnabledState(setGitHubMcpContextEnabled(true));
    publishRepositoryChange(repositories);
    setRepoDraft("");
  }, [publishRepositoryChange, repoDraft]);

  const chooseGitHubRepository = useCallback((repoId) => {
    const repositories = selectGitHubMcpRepository(repoId);
    if (repositories.length) {
      setGithubContextEnabledState(setGitHubMcpContextEnabled(true));
    }
    publishRepositoryChange(repositories);
  }, [publishRepositoryChange]);

  const deleteGitHubRepository = useCallback((repoId) => {
    const repositories = removeGitHubMcpRepository(repoId);
    publishRepositoryChange(repositories);
  }, [publishRepositoryChange]);

  useEffect(() => {
    window.addEventListener("storage", refreshGitHubRepositories);
    return () => window.removeEventListener("storage", refreshGitHubRepositories);
  }, [refreshGitHubRepositories]);

  useEffect(() => {
    if (!status || !selectedGitHubConnectorId) return;
    const stillAvailable = githubConnectors.some((connector) => connector.id === selectedGitHubConnectorId);
    if (!stillAvailable) {
      selectGitHubConnector("");
    }
  }, [status, githubConnectors, selectedGitHubConnectorId, selectGitHubConnector]);

  return (
    <section className="min-w-0 space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 text-sm font-medium text-slate-900">MCP connections</div>
          <div className="flex min-w-0 flex-wrap gap-2">
            <StatusPill active={!!status?.ok} label={status?.ok ? "Product API ready" : "Checking"} />
            <StatusPill
              active={!!status?.externalConnectorsEnabled}
              label={status?.externalConnectorsEnabled ? "External connectors enabled" : "External connectors disabled"}
            />
          </div>
        </div>
        <p className="text-sm text-slate-600">
          Create a hosted xHandle MCP token for Claude Desktop, VS Code, and Codex, then add GitHub context for Collaborator when needed.
        </p>
      </div>

      {loading && (
        <div className="rounded-xl border bg-white p-3 text-sm text-slate-600">Loading MCP status...</div>
      )}

      {statusError && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{statusError}</div>
      )}

      {status && (
        <div className="space-y-5">
          <div className="rounded-xl border bg-white p-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-slate-900">MCP setup health</div>
                <div className="mt-1 text-xs text-slate-500">
                  Local Claude/Codex setup is separate from backend-mediated GitHub context for Collaborator.
                </div>
              </div>
              <StatusPill active={mcpHealthItems.every((item) => item.ok)} label={mcpHealthItems.every((item) => item.ok) ? "Ready" : "Needs setup"} />
            </div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {mcpHealthItems.map((item) => (
                <HealthItem key={item.label} {...item} />
              ))}
            </div>
          </div>

          <div className="rounded-xl border bg-white p-1">
            <div className="grid min-w-0 gap-1 sm:grid-cols-2 lg:grid-cols-4" role="tablist" aria-label="MCP settings subviews">
              {MCP_SETTINGS_SUBVIEWS.map((subview) => {
                const active = activeMcpSubview === subview.id;
                return (
                  <button
                    key={subview.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={[
                      "min-w-0 rounded-lg px-3 py-2 text-sm font-medium transition",
                      active
                        ? "bg-slate-900 text-white shadow-sm"
                        : "bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                    ].join(" ")}
                    onClick={() => setActiveMcpSubview(subview.id)}
                  >
                    {subview.label}
                  </button>
                );
              })}
            </div>
          </div>

          {activeMcpSubview === "hosted" && (
          <div className="rounded-xl border bg-white p-4">
            <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-slate-900">Hosted xHandle MCP</div>
                  <div className="mt-1 text-xs text-slate-500">
                    Claude Desktop, VS Code, and Codex connect through the remote stdio bridge and read the profile context published by this deployed xHandle account.
                  </div>
                </div>
                <StatusPill active={hostedTokens.some((token) => !token.revokedAt)} label={hostedTokens.some((token) => !token.revokedAt) ? "Token ready" : "Create token"} />
              </div>
              <code className="mt-3 block overflow-x-auto rounded bg-white px-2 py-2 text-xs text-slate-800">
                {hostedMcpUrl()}
              </code>
            </div>

            <div className="grid min-w-0 gap-5">
              <div className="min-w-0 rounded-lg border border-slate-200 p-4">
                <div className="font-medium text-slate-900">Scoped MCP tokens</div>
                <div className="mt-1 text-xs text-slate-500">
                  Tokens are stored hashed in Supabase. The raw token is shown only once after creation.
                </div>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <input
                    className="min-w-0 flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm"
                    value={hostedTokenName}
                    onChange={(event) => setHostedTokenName(event.target.value)}
                    placeholder="Claude Desktop"
                    aria-label="Hosted MCP token name"
                  />
                  <button
                    className="min-w-32 rounded-md border border-slate-950 bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:border-slate-300 disabled:bg-slate-300 disabled:text-slate-600 disabled:shadow-none"
                    onClick={createHostedToken}
                    disabled={hostedTokenBusy}
                    type="button"
                  >
                    {hostedTokenBusy ? "Creating..." : "Create token"}
                  </button>
                </div>
                {generatedHostedToken && (
                  <div className="mt-4 rounded border border-emerald-200 bg-emerald-50 p-3">
                    <div className="text-sm font-medium text-emerald-900">New token</div>
                    <code className="mt-2 block overflow-x-auto rounded bg-white px-2 py-2 text-xs text-emerald-900">
                      {generatedHostedToken}
                    </code>
                    <div className="mt-2 text-xs text-emerald-800">
                      Copy the config before closing Settings; xHandle will not show this token again.
                    </div>
                  </div>
                )}
                {hostedTokenError && (
                  <div className="mt-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    {hostedTokenError}
                  </div>
                )}
                <div className="mt-4 space-y-3">
                  {hostedTokens.length ? hostedTokens.map((token) => (
                    <div key={token.id} className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded border border-slate-200 bg-slate-50 p-2 text-sm">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900">{token.name || "MCP client"}</div>
                        <div className="break-words text-xs text-slate-500">
                          ....{token.last4} {token.lastUsedAt ? `last used ${token.lastUsedAt}` : "not used yet"} {token.revokedAt ? "revoked" : ""}
                        </div>
                      </div>
                      <button
                        className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                        onClick={() => revokeHostedToken(token.id)}
                        disabled={hostedTokenBusy || !!token.revokedAt}
                        type="button"
                      >
                        Revoke
                      </button>
                    </div>
                  )) : (
                    <div className="rounded bg-slate-50 p-3 text-sm text-slate-600">
                      No hosted MCP tokens have been created for this account yet.
                    </div>
                  )}
                </div>
              </div>

              <div className="min-w-0 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900">Claude Desktop and VS Code config</div>
                    <div className="mt-1 text-xs text-slate-500">
                      Use the same remote bridge for Claude Desktop, Codex CLI, ChatGPT desktop, or the Codex VS Code extension.
                    </div>
                  </div>
                  {claudeCopyMessage && <div className="max-w-full break-words text-xs text-slate-500">{claudeCopyMessage}</div>}
                </div>
                <div className="mt-3 grid min-w-0 gap-3 lg:grid-cols-2">
                  <div className="min-w-0">
                    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Claude config</div>
                    <pre className="mt-2 max-h-72 max-w-full overflow-auto rounded bg-slate-950 p-3 text-xs text-slate-50">
                      {hostedClaudeConfig}
                    </pre>
                    <button
                      className="mt-2 rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      onClick={copyHostedClaudeConfig}
                      type="button"
                    >
                      Copy Claude config
                    </button>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Codex/VS Code config</div>
                    <pre className="mt-2 max-h-72 max-w-full overflow-auto rounded bg-slate-950 p-3 text-xs text-slate-50">
                      {hostedCodexConfig}
                    </pre>
                    <button
                      className="mt-2 rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      onClick={copyHostedCodexConfig}
                      type="button"
                    >
                      Copy Codex config
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}

          {activeMcpSubview === "local" && (
          <div className="rounded-xl border bg-white p-3">
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">Local xHandle install path</div>
                  <div className="mt-1 text-xs text-slate-500">
                    Claude and Codex snippets use this path to launch the local stdio MCP server. Update it if xHandle is installed somewhere else.
                  </div>
                </div>
                <StatusPill active={!!snapshotStatus?.published} label={snapshotStatus?.published ? "Context published" : "No context published"} />
              </div>
              <input
                className="mt-3 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                value={installPath}
                onChange={(event) => setInstallPath(event.target.value)}
                aria-label="Local xHandle install path"
              />
              <div className="mt-2 text-xs text-slate-500">
                Secrets never belong in client config. External MCP clients receive only the sanitized context snapshot xHandle keeps published locally.
              </div>
            </div>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-slate-900">Claude Desktop setup</div>
                <div className="mt-1 text-xs text-slate-500">
                  Add this local stdio server config to Claude, restart Claude, then ask the validation prompts.
                </div>
              </div>
              <button
                className="rounded bg-slate-900 px-3 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
                onClick={validateClaudeTools}
                disabled={claudeValidationBusy}
                type="button"
              >
                {claudeValidationBusy ? "Validating..." : "Validate xHandle MCP tools"}
              </button>
            </div>
            <div className="mt-3 grid min-w-0 gap-3 lg:grid-cols-[1fr_0.85fr]">
              <div className="min-w-0">
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Claude config file</div>
                <code className="mt-1 block overflow-x-auto rounded bg-slate-100 px-2 py-2 text-xs text-slate-800">
                  {CLAUDE_DESKTOP_CONFIG_PATH}
                </code>
                <pre className="mt-2 max-h-60 max-w-full overflow-auto rounded bg-slate-950 p-3 text-xs text-slate-50">
                  {claudeConfig}
                </pre>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    className="rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    onClick={copyClaudeConfig}
                    type="button"
                  >
                    Copy config
                  </button>
                  <button
                    className="rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    onClick={copyClaudePrompts}
                    type="button"
                  >
                    Copy validation prompts
                  </button>
                  {claudeCopyMessage && (
                    <span className="text-xs text-slate-500">{claudeCopyMessage}</span>
                  )}
                </div>
              </div>
              <div className="min-w-0 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-sm font-medium text-slate-900">Validation prompts</div>
                <div className="mt-2 space-y-2">
                  {CLAUDE_VALIDATION_PROMPTS.map((prompt) => (
                    <div key={prompt} className="rounded bg-white px-2 py-2 text-xs text-slate-700">
                      {prompt}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {claudeValidationError && (
              <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                {claudeValidationError}
              </div>
            )}
            {claudeValidationResult && (
              <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                xHandle MCP tools ready for Claude Desktop. {claudeValidationResult.toolCount} tools and {claudeValidationResult.artifactCount} engineering artifact categories are available.
              </div>
            )}
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">Codex setup</div>
                  <div className="mt-1 text-xs text-slate-500">
                    Add this stdio server to Codex CLI, ChatGPT desktop, or the Codex VS Code extension so coding workflows can query the published xHandle snapshot.
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  Local-first: Codex sees only the auto-refreshed local snapshot.
                </div>
              </div>
              <div className="mt-3 grid min-w-0 gap-3 lg:grid-cols-[1fr_0.85fr]">
                <div className="min-w-0">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Codex MCP config</div>
                  <code className="mt-1 block overflow-x-auto rounded bg-white px-2 py-2 text-xs text-slate-800">
                    {CODEX_MCP_CONFIG_HINT}
                  </code>
                  <pre className="mt-2 max-h-60 max-w-full overflow-auto rounded bg-slate-950 p-3 text-xs text-slate-50">
                    {codexConfig}
                  </pre>
                  <div className="mt-2 text-xs text-slate-500">
                    In the Codex VS Code extension, you can also add a STDIO MCP server from the extension MCP settings and restart the extension.
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button
                      className="rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      onClick={copyCodexConfig}
                      type="button"
                    >
                      Copy Codex config
                    </button>
                    <button
                      className="rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      onClick={copyCodexPrompts}
                      type="button"
                    >
                      Copy Codex prompts
                    </button>
                  </div>
                </div>
                <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-3">
                  <div className="text-sm font-medium text-slate-900">Coding workflow prompts</div>
                  <div className="mt-2 space-y-2">
                    {CODEX_VALIDATION_PROMPTS.map((prompt) => (
                      <div key={prompt} className="rounded bg-slate-50 px-2 py-2 text-xs text-slate-700">
                        {prompt}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">Published xHandle context</div>
                  <div className="mt-1 text-xs text-slate-500">
                    xHandle automatically refreshes the sanitized local snapshot for Claude and Codex. The selected scope is remembered across sessions.
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={autoPublishEnabled}
                      onChange={(event) => toggleAutoPublish(event.target.checked)}
                    />
                    Auto-publish
                  </label>
                  <select
                    className="rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                    value={autoPublishScope}
                    onChange={(event) => chooseAutoPublishScope(event.target.value)}
                    aria-label="Automatic MCP publish scope"
                  >
                    <option value="workspace">Full workspace</option>
                    <option value="project">Active project</option>
                  </select>
                  <button
                    className="rounded bg-slate-900 px-3 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
                    onClick={() => publishProjectSnapshot("project")}
                    disabled={projectPublishBusy}
                    type="button"
                  >
                    {projectPublishBusy ? "Refreshing..." : "Refresh active project now"}
                  </button>
                  <button
                    className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 hover:bg-slate-100 disabled:opacity-50"
                    onClick={() => publishProjectSnapshot("workspace")}
                    disabled={projectPublishBusy}
                    type="button"
                  >
                    Refresh full workspace now
                  </button>
                </div>
              </div>
              {projectPublishError && (
                <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  {projectPublishError}
                </div>
              )}
              {snapshotStatusError && (
                <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  {snapshotStatusError}
                </div>
              )}
              {snapshotStatus?.published ? (
                <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                  Published {snapshotStatus.scope === "workspace" ? "full workspace" : (snapshotStatus.project?.name || "active project")} for local MCP clients at {snapshotStatus.publishedAt || "unknown time"}.
                  {Number.isFinite(Number(snapshotStatus.projectCount)) && Number.isFinite(Number(snapshotStatus.artifactCount)) && (
                    <span> Snapshot includes {snapshotStatus.projectCount} project(s), {snapshotStatus.artifactCount} artifact(s), active project id {snapshotStatus.activeProjectId || "unspecified"}.</span>
                  )}
                  {snapshotStatus.artifactTypeCounts && (
                    <div className="mt-2 grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-5">
                      <span>Requirements: {snapshotStatus.artifactTypeCounts.requirements || 0}</span>
                      <span>Hazards: {snapshotStatus.artifactTypeCounts.hazards || 0}</span>
                      <span>Functions: {snapshotStatus.artifactTypeCounts.functions || 0}</span>
                      <span>Verification: {snapshotStatus.artifactTypeCounts.verification || 0}</span>
                      <span>Architecture: {snapshotStatus.artifactTypeCounts.codeArchitecture || 0}</span>
                    </div>
                  )}
                  {snapshotStatus.dataQualityWarnings?.length > 0 && (
                    <div className="mt-2 text-xs">
                      Data quality warnings: {snapshotStatus.dataQualityWarnings.join(" | ")}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-3 rounded border border-slate-200 bg-white p-3 text-sm text-slate-600">
                  No xHandle context snapshot is published yet. Automatic publishing will create one as soon as project context is available.
                </div>
              )}
            </div>
          </div>
          )}

          {activeMcpSubview === "github" && (
          <>
          <div className="rounded-xl border bg-white p-3">
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={githubContextEnabled}
                  onChange={(event) => toggleGitHubContextEnabled(event.target.checked)}
                />
                <span>
                  <span className="block font-medium text-slate-900">Use GitHub MCP context in Collaborator</span>
                  <span className="mt-1 block text-xs text-slate-500">
                    Collaborator will request repo summary and recent activity only for the selected repository.
                  </span>
                </span>
              </label>
            </div>

            <div className="mb-4 rounded-lg border border-slate-200 p-3">
              <div className="font-medium text-slate-900">GitHub MCP token</div>
              <div className="mt-1 text-xs text-slate-500">
                Paste the full token, including the `github_pat_` or token-provider prefix. Store only read-only repository access. Private repos require that the token owner can read the repo.
              </div>
              <div className="mt-1 text-xs text-slate-500">
                Stored locally for this browser. xHandle sends it only to backend MCP routes in memory and never returns it in status, discovery, capability, context, or copied config.
              </div>
              <div className="mt-3 grid gap-2">
                <input
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  value={githubEndpointDraft}
                  onChange={(event) => setGithubEndpointDraft(event.target.value)}
                  placeholder={MCP_GITHUB_DEFAULT_ENDPOINT}
                  aria-label="GitHub MCP endpoint"
                />
                <input
                  type="password"
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  value={githubTokenDraft}
                  onChange={(event) => setGithubTokenDraft(event.target.value)}
                  placeholder={githubUserConnector.hasToken ? "Saved token unchanged" : "Paste GitHub MCP token"}
                  aria-label="GitHub MCP token"
                  autoComplete="off"
                />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  className="rounded bg-slate-900 px-3 py-2 text-sm text-white hover:bg-slate-800"
                  onClick={saveGitHubUserConnector}
                  type="button"
                >
                  Save token
                </button>
                <button
                  className="rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  onClick={clearGitHubUserConnector}
                  disabled={!githubUserConnector.hasToken}
                  type="button"
                >
                  Clear token
                </button>
                <span className="text-xs text-slate-500">
                  {githubUserConnector.hasToken ? `Saved token: ....${githubUserConnector.last4}` : "No local MCP token saved"}
                </span>
              </div>
            </div>

            <label className="block">
              <span className="font-medium text-slate-900">GitHub context connector</span>
              <span className="mt-1 block text-xs text-slate-500">
                Used by Collaborator only after a repo owner/name is available. The backend maps safe intents like repo summary and recent activity to read-only GitHub tools.
              </span>
              <select
                className="mt-3 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                value={selectedGitHubConnectorId}
                onChange={(event) => selectGitHubConnector(event.target.value)}
                disabled={!githubConnectors.length}
                aria-label="GitHub MCP connector"
              >
                <option value="">No GitHub MCP connector selected</option>
                {githubConnectors.map((connector) => (
                  <option key={connector.id} value={connector.id}>
                    {connectorLabel(connector)}
                  </option>
                ))}
              </select>
            </label>
            {!githubConnectors.length && (
              <div className="mt-2 text-xs text-slate-500">
                Configure a backend connector with GitHub tools to enable this selection.
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                className="rounded bg-slate-900 px-3 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
                onClick={testGitHubContext}
                disabled={githubTesting || !selectedGitHubConnectorId || !hasGitHubRepoHint}
              >
                {githubTesting ? "Testing GitHub context..." : "Test GitHub context"}
              </button>
              <span className="text-xs text-slate-500">
                Uses the selected repository and sends only GitHub owner/repo arguments. No xHandle workspace data is sent to GitHub MCP.
              </span>
            </div>
            {githubTestError && (
              <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                {githubTestError}
              </div>
            )}
            {githubTestResult && (
              <div className="mt-3 rounded bg-slate-50 p-3 text-sm">
                <div className="font-medium text-slate-900">
                  GitHub context result for {githubTestResult.repository}
                </div>
                <div className="mt-2 space-y-2">
                  {githubTestResult.contexts.map((context) => (
                    <div key={context.label}>
                      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        {context.label}
                      </div>
                      <div className="mt-1 text-slate-700">{previewText(context.text) || "No shaped context returned."}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-xl border bg-white p-3">
            <div className="font-medium text-slate-900">GitHub MCP repositories</div>
            <div className="mt-1 text-xs text-slate-500">
              Add `owner/repo` or a GitHub URL. Stored as repo references only; credentials stay in your browser token configuration.
            </div>
            <div className="mt-1 text-xs text-slate-500">
              If testing returns 404 or 422 for a private repo, confirm the owner/repo spelling and that the saved token can read that repository.
            </div>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input
                className="min-w-0 flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm"
                value={repoDraft}
                onChange={(event) => setRepoDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") addGitHubRepository();
                }}
                placeholder="owner/repo or GitHub URL"
                aria-label="GitHub MCP repository"
              />
              <button
                className="rounded bg-slate-900 px-3 py-2 text-sm text-white hover:bg-slate-800"
                onClick={addGitHubRepository}
                type="button"
              >
                Add repo
              </button>
            </div>
            {githubRepositories.length ? (
              <div className="mt-3 space-y-2">
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={selectedGitHubRepository?.id || ""}
                  onChange={(event) => chooseGitHubRepository(event.target.value)}
                  aria-label="Selected GitHub MCP repository"
                >
                  {githubRepositories.map((repo) => (
                    <option key={repo.id} value={repo.id}>{repo.label}</option>
                  ))}
                </select>
                <div className="flex flex-wrap items-center justify-between gap-2 rounded bg-slate-50 p-2 text-sm">
                  <span className="text-slate-700">
                    Selected: {selectedGitHubRepository?.label || "No repository selected"}
                  </span>
                  <button
                    className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                    onClick={() => deleteGitHubRepository(selectedGitHubRepository?.id)}
                    disabled={!selectedGitHubRepository}
                    type="button"
                  >
                    Remove selected
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-3 rounded bg-slate-50 p-3 text-sm text-slate-600">
                No GitHub MCP repositories are selected yet.
              </div>
            )}
          </div>
          </>
          )}

          {activeMcpSubview === "diagnostics" && (
          <div className="rounded-xl border bg-white p-3">
            <div className="text-sm font-medium text-slate-900">Advanced diagnostics</div>
            <div className="mt-3 space-y-3">
              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-xl border bg-white p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Mode</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{status.mode || "unknown"}</div>
                </div>
                <div className="rounded-xl border bg-white p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Standalone required</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {status.standaloneMcpServerRequired ? "Yes" : "No"}
                  </div>
                </div>
                <div className="rounded-xl border bg-white p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">External connectors</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{status.externalConnectorCount || 0}</div>
                </div>
              </div>

              <div className="rounded-xl border bg-white p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-medium text-slate-900">Configured connectors</div>
                    <div className="mt-1 text-xs text-slate-500">Managed by backend environment configuration.</div>
                  </div>
                  <button
                    className="rounded bg-slate-900 px-3 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
                    onClick={testDiscovery}
                    disabled={discovering}
                  >
                    {discovering ? "Testing..." : "Test discovery"}
                  </button>
                </div>

                {connectors.length ? (
                  <div className="mt-3 divide-y">
                    {connectors.map((connector) => (
                      <div key={connector.id} className="py-2 text-sm">
                        <div className="font-medium text-slate-900">{connectorLabel(connector)}</div>
                        {connector.description && <div className="text-slate-500">{connector.description}</div>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-3 rounded bg-slate-50 p-3 text-sm text-slate-600">
                    No external MCP connectors are configured for this backend.
                  </div>
                )}
              </div>

              {discoveryError && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  {discoveryError}
                </div>
              )}

              {discoveryResult && (
                <div className="rounded-xl border bg-white p-3 text-sm">
                  <div className="font-medium text-slate-900">Discovery result</div>
                  <div className="mt-2 space-y-2">
                    {discoveryResult.map((entry) => (
                      <div key={entry.connector?.id || entry.connector?.name} className="rounded bg-slate-50 p-2">
                        <div className="font-medium text-slate-900">{connectorLabel(entry.connector)}</div>
                        <div className={entry.ok ? "text-emerald-700" : "text-rose-700"}>
                          {entry.ok ? `${entry.tools?.length || 0} tool(s) discovered` : entry.error || "Discovery failed"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {capabilityResult && (
                <div className="rounded-xl border bg-white p-3 text-sm">
                  <div className="font-medium text-slate-900">Capability result</div>
                  <div className="mt-2 space-y-2">
                    {capabilityResult.map((entry) => (
                      <div key={entry.connector?.id || entry.connector?.name} className="rounded bg-slate-50 p-2">
                        <div className="font-medium text-slate-900">{connectorLabel(entry.connector)}</div>
                        {entry.ok ? (
                          <div className="mt-1 flex flex-wrap gap-2">
                            {(entry.capabilities || []).map((capability) => (
                              <span
                                key={capability.intent}
                                className={[
                                  "rounded-full px-2 py-1 text-xs",
                                  capability.available ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500",
                                ].join(" ")}
                              >
                                {capability.intent}{capability.available ? `: ${capability.toolName}` : ": unavailable"}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="text-rose-700">{entry.error || "Capability resolution failed"}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      )}
    </section>
  );
}
